/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { PERMISSIONS } from '@squiz/dxp-auth';
import { filterByKeyword } from '@squiz/dxp-console-support';
import {
  EmptyState,
  EmptyStateProps,
  ICON_INFO_WITH_LIST_BLACK_AND_WHITE,
  ICON_INFO_WITH_SEARCH_BLACK_AND_WHITE,
  SPINNER_TYPE_REGULAR,
  SearchFilter,
  SearchFilterProps,
  Spinner,
  Table,
  TableCell,
  TableContent,
  TableProps,
} from '@squiz/sds';
import { getCapabilityPath } from 'apps/console-core/src/Utilities/GetCapabilityPath/GetCapabilityPath';
import { hasPermission } from 'apps/console-core/src/Utilities/HasPermission/HasPermission';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CONFIGURATION_STRINGS } from '../../../../Constants/Strings.constants';
import { EntityJob } from '../../../../Services/job-runner';
import { useJobs } from '../../../../Services/job-runner/src/Hooks/UseJobs/UseJobs.hook';
import { JOBS_LIST_DATA_IDS } from './JobsList.constants';

/**
 * View all available Jobs.
 */
export const JobsList = (): JSX.Element => {
  const { t } = useTranslation();

  // Use Jobs hook to fetch latest jobs
  const [jobs] = useJobs();

  // Store filter string in state
  const [filterValue, setFilterValue] = useState<string>('');

  const hasProfileAccess = hasPermission(PERMISSIONS.dxpJobRunner.JOB_READ);

  // Handle navigation
  const navigate = useNavigate();
  const handleNavigation = useCallback(
    (itemPath: string): void => {
      navigate(itemPath);
    },
    [navigate],
  );

  // Labels
  const labels = useMemo(() => {
    return {
      description: t(CONFIGURATION_STRINGS.description),
      lastRun: t(CONFIGURATION_STRINGS.lastRun),
      latestVersion: t(CONFIGURATION_STRINGS.latestVersion),
      name: t(CONFIGURATION_STRINGS.name),
      noJobsAvailable: t(CONFIGURATION_STRINGS.noJobsAvailable),
      noMatches: t(CONFIGURATION_STRINGS.noJobsFound),
      noMatchingJobs: t(CONFIGURATION_STRINGS.noMatchingJobs),
      searchPlaceholder: t(CONFIGURATION_STRINGS.jobsSearchPlaceholder),
      value: t(CONFIGURATION_STRINGS.value),
    };
  }, [t]);

  const tableColumns: Array<TableCell> = [
    {
      content: labels.name,
      key: `name`,
    },
    {
      content: labels.description,
      key: `description`,
    },
    {
      content: labels.latestVersion,
      key: `version`,
    },
  ];

  const tableRows = useMemo(() => {
    if (!jobs) {
      return [];
    }

    return jobs.map((job) => {
      const goToJob = (event: React.MouseEvent): void => {
        event.preventDefault();
        const jobPath = getCapabilityPath(`/job-runner/jobs/${job.name}`);
        handleNavigation(jobPath);
      };

      const jobDescriptionDiv = <div className="job-description">{job.description}</div>;

      const jobLink = hasProfileAccess ? (
        <button className="job-name-link" data-id={JOBS_LIST_DATA_IDS.jobNameButton} onClick={goToJob}>
          {job.name}
        </button>
      ) : (
        <span className="job-name">{job.name}</span>
      );

      const jobVersionDiv = <span className="job-version">{job.version}</span>;

      // Add `_keywords` prop to each job to enable keyword filtering
      const keywords = `${job.name} ${job.description}`;

      return {
        _keywords: keywords,
        job: { ...job, description: jobDescriptionDiv, name: jobLink, version: jobVersionDiv },
      };
    });
  }, [handleNavigation, hasProfileAccess, jobs]);

  const filteredRows = useMemo(() => {
    const filteredRows = filterByKeyword(tableRows, filterValue);
    const filteredJobs: Array<EntityJob> = [];

    for (const rowIndex in filteredRows) {
      if (rowIndex && filteredRows[rowIndex]) {
        const rowObj: { job: EntityJob; _keywords: string } = filteredRows[rowIndex] as {
          job: EntityJob;
          _keywords: string;
        };
        const job: EntityJob = rowObj.job;
        filteredJobs.push(job);
      }
    }

    return filteredJobs;
  }, [filterValue, tableRows]);

  const noJobs = jobs && jobs.length === 0;
  if (noJobs) {
    const emptyStateProps: EmptyStateProps = {
      caption: labels.noJobsAvailable,
      dataId: JOBS_LIST_DATA_IDS.noJobsEmptyState,
      svg: ICON_INFO_WITH_LIST_BLACK_AND_WHITE,
    };

    return <EmptyState {...emptyStateProps} />;
  }

  const getJobsList = (): JSX.Element => {
    if (filteredRows.length === 0) {
      const emptyStateProps: EmptyStateProps = {
        caption: labels.noMatchingJobs,
        dataId: JOBS_LIST_DATA_IDS.noMatchingJobsEmptyState,
        svg: ICON_INFO_WITH_SEARCH_BLACK_AND_WHITE,
      };
      return <EmptyState {...emptyStateProps} />;
    }
    return (
      <div>
        <Table {...tableProps} />
      </div>
    );
  };

  const tableContent: TableContent = { columns: tableColumns, rows: filteredRows };
  const tableProps: TableProps = {
    classes: `jobs__table`,
    emptyText: labels.noMatches,
    perPageDefault: 20,
    perPageOptions: [20, 50, 100],
    tableContent,
  };

  const filterProps = {
    filterValue,
    placeholder: labels.searchPlaceholder,
    setFilterValue,
  } as SearchFilterProps;

  if (!jobs) {
    return (
      <div className="loading">
        <Spinner type={SPINNER_TYPE_REGULAR} />
      </div>
    );
  }

  return (
    <div className="jobs__wrapper">
      <div className="jobs__filter" data-id={JOBS_LIST_DATA_IDS.searchBar}>
        <SearchFilter {...filterProps} />
      </div>
      {getJobsList()}
    </div>
  );
};
